/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import Dialog from 'components/ChoreoSystem/Dialog/Dialog';
import { Check } from 'components/ChoreoSystem/Icons/generated';
import LoginCard from 'modules/login/LoginCard';
import SignupCard from 'modules/signup/SignupCard';
import useStyles from './AuthDialog.styles';

export type AuthDialogMode = 'signIn' | 'signUp';

/**
 * Id of the active card's title ("Sign In" / "Sign Up"). It names the dialog
 * through `aria-labelledby` — the convention the rest of the app's dialogs
 * follow — and is also the element focus moves to when the card is swapped,
 * so no extra translated string is needed for either.
 */
export const AUTH_DIALOG_TITLE_ID = 'auth-dialog-title';

interface AuthDialogProps {
  open: boolean;
  mode: AuthDialogMode;
  onModeChange: (mode: AuthDialogMode) => void;
  onClose: () => void;
  isMobile: boolean;
  /**
   * Whether a Copilot prompt is waiting to be replayed after sign in.
   *
   * The dialog covers the box the prompt was typed into, so without something
   * saying otherwise the visitor is asked to sign in with their sentence
   * nowhere on screen — the moment it looks like the prompt was thrown away
   * and signing in is an unrelated toll. The notice is the receipt for it.
   */
  hasPendingPrompt?: boolean;
  // Sign in
  onEmailClick: () => void;
  onGithubClick: () => void;
  onGoogleClick: () => void;
  onMicrosoftClick: () => void;
  onAnonymousClick: () => void;
  onEnterpriseLoginClick: (userEmail: string) => void;
  // Sign up
  onEmailSignUpClick: () => void;
  onGithubSignUpClick: () => void;
  onGoogleSignUpClick: () => void;
  onMicrosoftSignUpClick: () => void;
}

/**
 * Hosts the sign in and sign up cards in a modal over the Copilot landing
 * surface.
 *
 * Both cards are rendered untouched — `AuthenticationFormCard` already supplies
 * the logo, title, region selector and policy links — so the dialog adds no
 * chrome of its own beyond the close button. Switching between the two modes
 * swaps the card rather than navigating, which keeps the prompt the user typed
 * on screen behind the dialog.
 */
export default function AuthDialog(props: AuthDialogProps) {
  const {
    open,
    mode,
    onModeChange,
    onClose,
    isMobile,
    hasPendingPrompt = false,
    onEmailClick,
    onGithubClick,
    onGoogleClick,
    onMicrosoftClick,
    onAnonymousClick,
    onEnterpriseLoginClick,
    onEmailSignUpClick,
    onGithubSignUpClick,
    onGoogleSignUpClick,
    onMicrosoftSignUpClick,
  } = props;

  const classes = useStyles();

  // A closing dialog stays mounted through its fade, so following the host's
  // reset would flip the card and drop the receipt in front of the visitor.
  const [shown, setShown] = useState({ mode, hasPendingPrompt });
  if (
    open &&
    (shown.mode !== mode || shown.hasPendingPrompt !== hasPendingPrompt)
  ) {
    setShown({ mode, hasPendingPrompt });
  }

  // Swapping the card unmounts the very control the user activated. MUI's
  // TrapFocus then parks focus on the dialog root, which announces nothing and
  // makes the keyboard user tab from the top again. Move focus to the new
  // card's title instead: it sits inside the trap, so `contain()` leaves it
  // alone, and screen readers read out "Sign Up, heading" as confirmation that
  // the card changed. The first render of each opening is skipped so this
  // never competes with TrapFocus's own initial focus, which runs off `open`.
  const lastOpenModeRef = useRef<AuthDialogMode | null>(null);
  useEffect(() => {
    if (!open) {
      lastOpenModeRef.current = null;
      return;
    }
    const previousMode = lastOpenModeRef.current;
    lastOpenModeRef.current = shown.mode;
    if (previousMode !== null && previousMode !== shown.mode) {
      document.getElementById(AUTH_DIALOG_TITLE_ID)?.focus();
    }
  }, [open, shown.mode]);

  return (
    <Dialog
      testId="auth-dialog"
      open={open}
      onClose={onClose}
      aria-labelledby={AUTH_DIALOG_TITLE_ID}
      maxWidth="sm"
      fullWidth
    >
      <Box className={classes.authDialogContent}>
        {/*
          Above the card rather than inside it: `LoginCard` and `SignupCard`
          are the same components the full page sign in route renders, where
          there is no prompt to have saved.

          `role="status"` rather than an alert: it is already on screen when
          the dialog opens, so it is read as part of the dialog rather than
          interrupting, and it is reannounced if the wording changes with the
          card.
        */}
        {shown.hasPendingPrompt && (
          <Box
            role="status"
            className={classes.promptSavedNotice}
            data-testid="auth-dialog-prompt-saved"
          >
            <Check className={classes.promptSavedIcon} />
            <Typography variant="body2">
              {shown.mode === 'signIn' ? (
                <FormattedMessage
                  id="modules.login.authDialog.promptSaved.signIn"
                  defaultMessage="We've saved your prompt. Sign in to get started."
                />
              ) : (
                <FormattedMessage
                  id="modules.login.authDialog.promptSaved.signUp"
                  defaultMessage="We've saved your prompt. Sign up to get started."
                />
              )}
            </Typography>
          </Box>
        )}

        {shown.mode === 'signIn' ? (
          <LoginCard
            // Invitations never reach this dialog — they keep the original
            // full page layout, which carries the "you have been invited to X"
            // messaging this surface has no room for.
            isInvitation={false}
            invitedOrgName=""
            isMobile={isMobile}
            titleId={AUTH_DIALOG_TITLE_ID}
            onEmailClick={onEmailClick}
            onGithubClick={onGithubClick}
            onGoogleClick={onGoogleClick}
            onMicrosoftClick={onMicrosoftClick}
            onAnonymousClick={onAnonymousClick}
            onEnterpriseLoginClick={onEnterpriseLoginClick}
            onSignUpClick={() => onModeChange('signUp')}
          />
        ) : (
          <SignupCard
            isMobile={isMobile}
            titleId={AUTH_DIALOG_TITLE_ID}
            onEmailClick={onEmailSignUpClick}
            onGithubClick={onGithubSignUpClick}
            onGoogleClick={onGoogleSignUpClick}
            onMicrosoftClick={onMicrosoftSignUpClick}
            onSignInClick={() => onModeChange('signIn')}
          />
        )}
      </Box>
    </Dialog>
  );
}
